import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosConfig";

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("access_token");
  const [userData, setUserData] = useState(null);
  const [profileComplete, setProfileComplete] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(null);
  const [hasEverBeenSubscribed, setHasEverBeenSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const publicRoutes = [
    '/register',
    '/login',
    '/profile-setup',
    '/checkout',
    '/welcome-back',
    '/update-payment',
    '/privacy-policy',
    '/terms-of-service'
  ];

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!token) {
        setLoading(false);
        setProfileComplete(false);
        setIsSubscribed(false);
        return;
      }

      try {
        const bootstrapResponse = await axiosInstance.get('/web/bootstrap/');
        const { user, profile, subscription } = bootstrapResponse.data;

        setUserData(user);
        setProfileComplete(Boolean(profile?.profile_complete));
        console.log('Bootstrap status:', bootstrapResponse.data);

        if (profile?.profile_complete && subscription) {
          setIsSubscribed(subscription.is_subscribed);
          setHasEverBeenSubscribed(
            Boolean(subscription.has_ever_been_subscribed)
          );
          setSubscriptionStatus(
            subscription.subscription_details?.status || null
          );
        } else {
          setIsSubscribed(false);
          setHasEverBeenSubscribed(false);
          setSubscriptionStatus(null);
        }
      } catch (error) {
        console.error("Error in checkUserStatus:", error.response || error);
        if (error.response?.status === 401) {
          localStorage.removeItem("access_token");
        }
        setProfileComplete(false);
        setIsSubscribed(false);
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle public routes first
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  // Handle authentication
  if (!token || !userData) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Handle profile completion
  if (!profileComplete && location.pathname !== "/profile-setup") {
    return <Navigate to="/profile-setup" />;
  }

  // Past-due lockout — fires first because past_due users still have
  // is_subscribed=true (Stripe grace window). Hard lockout: they cannot
  // access the platform until their card is fixed.
  if (profileComplete && subscriptionStatus === 'past_due'
      && location.pathname !== "/update-payment") {
    return <Navigate to="/update-payment" />;
  }

  // Handle subscription check — lapsed (previously paid) users go to the
  // warm welcome-back screen; brand-new/never-paid users go to generic
  // checkout.
  if (profileComplete && !isSubscribed) {
    if (hasEverBeenSubscribed && location.pathname !== "/welcome-back") {
      return <Navigate to="/welcome-back" />;
    }
    if (!hasEverBeenSubscribed && location.pathname !== "/checkout") {
      return <Navigate to="/checkout" />;
    }
  }

  // Special case: After completing profile — send to the appropriate
  // payment surface (welcome-back for lapsed users, checkout for new).
  if (profileComplete && location.pathname === "/profile-setup") {
    return <Navigate to={hasEverBeenSubscribed ? "/welcome-back" : "/checkout"} />;
  }

  // Allow access if all conditions are met
  return children;
};

export default ProtectedRoute;
